import { Link } from "react-router";
import {
  ArrowRight,
  BrainCircuit,
  Camera,
  CheckCircle2,
  CreditCard,
  FileSearch2,
  FileText,
  Gauge,
  GitCompareArrows,
  Layers,
  Link as LinkIcon,
  Lock,
  Mail,
  Receipt,
  Route,
  Scale,
  Sparkles,
  Users,
  Workflow,
} from "lucide-react";
import { useAuth } from "../../../lib/auth/useAuth";
import { Reveal } from "../../components/public/motion";

const PILLARS = [
  {
    eyebrow: "Capture",
    title: "Receipts, invoices, mileage — everywhere your money lives.",
    body:
      "Forward an email. Snap a photo in chat. Link your card. Upload a CSV. However proof arrives, Proofly extracts the amount, vendor, tax fields, and category automatically.",
    bullets: [
      { icon: Mail, text: "Email & chat forwarding" },
      { icon: Camera, text: "Mobile capture with instant OCR" },
      { icon: CreditCard, text: "Card-linked auto-match" },
      { icon: Route, text: "Mileage from drop pin to deduction" },
    ],
  },
  {
    eyebrow: "AI",
    title: "AI that finishes the work — not just suggests it.",
    body:
      "Categorization, extraction, reconciliation, and anomaly detection run continuously in the background. Every suggestion shows the evidence — so your CPA and your auditor stay comfortable.",
    bullets: [
      { icon: Sparkles, text: "Auto-categorization against your taxonomy" },
      { icon: BrainCircuit, text: "Narrative generation for every expense" },
      { icon: GitCompareArrows, text: "Duplicate & anomaly detection" },
      { icon: FileSearch2, text: "Cited evidence on every suggestion" },
    ],
  },
  {
    eyebrow: "Control",
    title: "Policies and approvals that explain themselves.",
    body:
      "Set deductibility rules in plain English. Approve in bulk. Flag anomalies before they hit the books. Every action is logged so you can answer not just 'what' but 'why' and 'who.'",
    bullets: [
      { icon: Scale, text: "Plain-English tax policies" },
      { icon: Workflow, text: "Multi-step approval chains" },
      { icon: Layers, text: "Batch review — 200 items in one sweep" },
      { icon: Users, text: "Roles for admin, manager, submitter, accountant" },
    ],
  },
  {
    eyebrow: "Audit",
    title: "Always ready. Always explainable.",
    body:
      "The audit score is live. The export is one click. The public auditor view needs no login. This is the category-defining piece — built for the moment an accountant or the IRS asks 'can you prove it?'",
    bullets: [
      { icon: Gauge, text: "Live audit score with drill-down" },
      { icon: FileText, text: "Auditor-ready PDF packet" },
      { icon: LinkIcon, text: "Public auditor share link" },
      { icon: Lock, text: "7-year retention on Team" },
    ],
  },
];

const FEATURE_GRID = [
  {
    icon: Receipt,
    title: "Expenses",
    body: "Every transaction, already explained. Categorized, matched to a vendor, and linked to its receipt.",
  },
  {
    icon: FileText,
    title: "Invoices",
    body: "Send, track, and reconcile invoices. Overdue flags and payment detection are automatic.",
  },
  {
    icon: Route,
    title: "Mileage",
    body: "GPS-backed mileage with IRS-rate calculation. Tax-deductible miles without the spreadsheet.",
  },
  {
    icon: Scale,
    title: "Tax policies",
    body: "Set what's deductible, what's taxable, and which categories apply. In plain English.",
  },
  {
    icon: Users,
    title: "Employees",
    body: "Submit, approve, and reconcile on behalf of a team. Per-role views and per-person policies.",
  },
  {
    icon: CreditCard,
    title: "Card linking",
    body: "Connect corporate or personal cards. Charges arrive pre-matched to the right receipt.",
  },
  {
    icon: Layers,
    title: "Batch processing",
    body: "Categorize, approve, reject, or request info on 200 items at once — with a full audit trail.",
  },
  {
    icon: Workflow,
    title: "Review workflows",
    body: "Manager-first, CFO-first, or auto-approve under threshold. Mix and match by category.",
  },
  {
    icon: Gauge,
    title: "Audit dashboard",
    body: "One score that tells you whether your records are ready for outside eyes. Today.",
  },
  {
    icon: FileSearch2,
    title: "Audit simulation",
    body: "Run a mock audit on your own books. Find the weak spots before your auditor does.",
  },
  {
    icon: LinkIcon,
    title: "Auditor view",
    body: "A secure, read-only share link. No account required. Revoke access in one click.",
  },
  {
    icon: Lock,
    title: "Retention & security",
    body: "SOC 2 Type II (Enterprise). Configurable retention. Customer-managed export anytime.",
  },
];

const INTEGRATIONS = [
  "QuickBooks Online",
  "Xero",
  "Stripe",
  "Plaid",
  "Gmail",
  "Outlook",
  "Slack",
  "Teams",
  "Dropbox",
  "Google Drive",
  "Notion",
  "Zapier",
];

export function Features() {
  const { session } = useAuth();
  const ctaHref = session ? "/inbox" : "/login";
  const ctaLabel = session ? "Open app" : "Start free";

  return (
    <div className="w-full">
      {/* Hero */}
      <section
        className="relative"
        style={{
          background:
            "radial-gradient(600px 360px at 80% 0%, rgba(46,91,255,0.12), transparent 60%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-14 pb-12 md:pt-20 md:pb-16">
          <div className="max-w-[820px]">
            <Reveal y={10}>
              <div className="text-xs uppercase tracking-[0.12em] font-semibold text-[var(--brand-700)] mb-4">
                Features
              </div>
            </Reveal>
            <Reveal y={20} delay={60}>
              <h1
                className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.03em]"
                style={{
                  fontSize: "clamp(40px, 6vw, 64px)",
                  lineHeight: 1.05,
                }}
              >
                Whole stack.
                <br />
                <span className="bg-gradient-to-br from-[var(--brand-600)] to-[var(--brand-900)] bg-clip-text text-transparent">
                  One record.
                </span>
              </h1>
            </Reveal>
            <Reveal y={16} delay={140}>
              <p className="mt-6 text-[18px] text-[var(--ink-700)] max-w-[640px]">
                Capture, categorize, control, export. Every dollar
                explainable — every day, not just in April.
              </p>
            </Reveal>
            <Reveal y={10} delay={220}>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Link
                  to={ctaHref}
                  className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white font-semibold transition-colors shadow-[0_10px_24px_-10px_rgba(10,15,30,0.35)]"
                >
                  {ctaLabel} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full border border-[var(--ink-200)] bg-white hover:bg-[var(--ink-50)] text-[var(--ink-900)] font-semibold transition-colors"
                >
                  See pricing
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pillars (alternating) */}
      {PILLARS.map((p, i) => (
        <section
          key={p.title}
          className={i % 2 === 1 ? "bg-[var(--ink-50)] border-y border-[var(--ink-200)]" : ""}
        >
          <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-24">
            <div
              className={[
                "grid gap-12 md:gap-20 items-center",
                "md:grid-cols-2",
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : "",
              ].join(" ")}
            >
              <div>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
                  {p.eyebrow}
                </div>
                <h2
                  className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.02em]"
                  style={{ fontSize: "clamp(28px, 4vw, 42px)", lineHeight: 1.15 }}
                >
                  {p.title}
                </h2>
                <p className="mt-5 text-[17px] leading-relaxed text-[var(--ink-700)] max-w-[520px]">
                  {p.body}
                </p>
                <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {p.bullets.map((b) => (
                    <li
                      key={b.text}
                      className="flex items-center gap-3 p-3 rounded-xl border border-[var(--ink-200)] bg-white text-[14px] text-[var(--ink-900)]"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-[var(--brand-50)] text-[var(--brand-700)] shrink-0">
                        <b.icon className="w-4 h-4" strokeWidth={1.8} />
                      </span>
                      {b.text}
                    </li>
                  ))}
                </ul>
              </div>
              <PillarMock index={i} />
            </div>
          </div>
        </section>
      ))}

      {/* Feature grid */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[680px] mb-14">
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
            All features
          </div>
          <h2
            className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)]"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
          >
            Twelve capabilities. One clean record.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURE_GRID.map((f) => (
            <div
              key={f.title}
              className="p-6 rounded-2xl border border-[var(--ink-200)] bg-white hover:border-[var(--brand-300)] transition-colors"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--ink-950)] text-white mb-4">
                <f.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <h3 className="text-[17px] font-semibold text-[var(--ink-950)] tracking-tight mb-1.5">
                {f.title}
              </h3>
              <p className="text-[14px] leading-relaxed text-[var(--ink-700)]">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section className="bg-[var(--ink-50)] border-y border-[var(--ink-200)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20">
          <div className="max-w-[680px] mb-10">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
              Integrations
            </div>
            <h2
              className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)]"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
            >
              Works with the tools you already use.
            </h2>
            <p className="mt-4 text-[17px] text-[var(--ink-700)]">
              No rip-and-replace. Proofly sits on top of your accounting and
              card stack, enriching records wherever they flow.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {INTEGRATIONS.map((name) => (
              <div
                key={name}
                className="p-4 rounded-xl border border-[var(--ink-200)] bg-white flex items-center justify-center text-sm font-semibold text-[var(--ink-900)]"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
        <h2
          className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)] max-w-[680px] mx-auto"
          style={{ fontSize: "clamp(30px, 4vw, 48px)", lineHeight: 1.12 }}
        >
          Every dollar, answered.
        </h2>
        <p className="mt-5 text-[18px] text-[var(--ink-700)] max-w-[560px] mx-auto">
          Start free and see how much your records improve in one weekend.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={ctaHref}
            className="inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full bg-[var(--ink-950)] hover:bg-[var(--ink-900)] text-white font-semibold transition-colors"
          >
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center h-12 px-7 rounded-full border border-[var(--ink-200)] bg-white hover:bg-[var(--ink-50)] text-[var(--ink-900)] font-semibold transition-colors"
          >
            Compare plans
          </Link>
        </div>
      </section>
    </div>
  );
}

function PillarMock({ index }: { index: number }) {
  // Four distinct minimal mocks per pillar. Abstract, not photo-real, so they
  // stay sharp at any screen size and don't pretend to be the real product.
  switch (index) {
    case 0:
      return <CaptureMock />;
    case 1:
      return <AIMock />;
    case 2:
      return <ControlMock />;
    case 3:
    default:
      return <AuditMock />;
  }
}

function MockCard({
  children,
  pad = "p-6",
}: {
  children: React.ReactNode;
  pad?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-[var(--ink-200)] bg-white ${pad}`}
      style={{ boxShadow: "var(--shadow-lg)" }}
    >
      {children}
    </div>
  );
}

function CaptureMock() {
  return (
    <MockCard>
      <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--ink-500)] mb-4">
        Incoming
      </div>
      {[
        { src: "Gmail", vendor: "Uber · $70.60", when: "2m ago" },
        { src: "Camera", vendor: "Logi Ergo Mouse · $73.48", when: "6m ago" },
        { src: "Card", vendor: "Lyft · $32.96", when: "12m ago" },
        { src: "Upload", vendor: "Branch · $381.16", when: "23m ago" },
      ].map((row) => (
        <div
          key={row.vendor}
          className="flex items-center gap-3 py-3 border-b border-[var(--ink-100)] last:border-0"
        >
          <div className="w-9 h-9 rounded-lg bg-[var(--brand-50)] text-[var(--brand-700)] flex items-center justify-center text-[11px] font-semibold shrink-0">
            {row.src}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[var(--ink-900)] truncate">
              {row.vendor}
            </div>
            <div className="text-[11px] text-[var(--ink-500)]">
              Extracted automatically
            </div>
          </div>
          <div className="text-[11px] text-[var(--ink-500)]">{row.when}</div>
          <CheckCircle2 className="w-4 h-4 text-[var(--success)]" />
        </div>
      ))}
    </MockCard>
  );
}

function AIMock() {
  return (
    <MockCard>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--brand-500)] to-[var(--brand-900)] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-[13px] font-semibold text-[var(--ink-900)]">
            Proofly AI suggestion
          </div>
          <div className="text-[11px] text-[var(--ink-500)]">
            Expense · $73.48 · Logi Tech Ergo Mouse
          </div>
        </div>
      </div>
      <div className="rounded-lg bg-[var(--ink-50)] p-4 text-[14px] text-[var(--ink-900)] leading-relaxed">
        <span className="font-semibold">Category:</span>{" "}
        <span className="px-1.5 py-0.5 rounded bg-[var(--brand-50)] text-[var(--brand-700)] font-semibold">
          Office equipment
        </span>
        <div className="mt-3 text-[13px] text-[var(--ink-700)]">
          <span className="font-semibold text-[var(--ink-900)]">Why:</span>{" "}
          Vendor profile classified as "consumer electronics"; amount aligns
          with your $500 de minimis threshold; last 3 Logi purchases went to
          Office equipment (
          <span className="underline decoration-dotted">see history</span>).
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { label: "Accept", primary: true },
          { label: "Change" },
          { label: "Reject" },
        ].map((b) => (
          <button
            key={b.label}
            className={[
              "h-9 rounded-md text-[13px] font-semibold",
              b.primary
                ? "bg-[var(--brand-600)] text-white"
                : "border border-[var(--ink-200)] text-[var(--ink-900)]",
            ].join(" ")}
          >
            {b.label}
          </button>
        ))}
      </div>
    </MockCard>
  );
}

function ControlMock() {
  return (
    <MockCard>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--ink-500)]">
            Review queue
          </div>
          <div className="text-[15px] font-semibold text-[var(--ink-950)]">
            12 selected · $4,218.33
          </div>
        </div>
        <div className="flex gap-2">
          <button className="h-8 px-3 rounded-md bg-[var(--success)] text-white text-[12px] font-semibold">
            Approve all
          </button>
          <button className="h-8 px-3 rounded-md border border-[var(--ink-200)] text-[var(--ink-700)] text-[12px] font-semibold">
            Request info
          </button>
        </div>
      </div>
      {[
        { v: "Delta Airlines · $612.40", p: "Travel · Over $500" },
        { v: "DoorDash · $48.19", p: "Meals · Needs justification" },
        { v: "AWS · $218.00", p: "Software · Auto-approved" },
      ].map((row, i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-2.5 border-b border-[var(--ink-100)] last:border-0"
        >
          <input type="checkbox" defaultChecked className="w-4 h-4 accent-[var(--brand-600)]" />
          <div className="flex-1 text-[13px] text-[var(--ink-900)] font-medium">
            {row.v}
          </div>
          <span className="text-[11px] font-semibold text-[var(--warning)] bg-[var(--warning-50)] px-2 py-0.5 rounded-full">
            {row.p}
          </span>
        </div>
      ))}
    </MockCard>
  );
}

function AuditMock() {
  return (
    <MockCard>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)]">
            Audit score
          </div>
          <div className="font-display font-semibold text-[56px] leading-none tracking-[-0.03em] text-[var(--ink-950)] mt-1">
            96
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-[var(--ink-500)] uppercase tracking-[0.08em] font-semibold">
            Trend
          </div>
          <svg viewBox="0 0 100 40" className="w-32 h-10 mt-1">
            <polyline
              fill="none"
              stroke="var(--brand-600)"
              strokeWidth="2"
              points="0,32 15,28 30,26 45,22 60,19 75,14 90,8"
            />
            <circle cx="90" cy="8" r="3" fill="var(--brand-600)" />
          </svg>
        </div>
      </div>
      {[
        { label: "Transactions with receipts", pct: 98 },
        { label: "Categorized expenses", pct: 100 },
        { label: "Vendors with tax forms", pct: 92 },
        { label: "Policies satisfied", pct: 95 },
      ].map((row) => (
        <div key={row.label} className="mb-3 last:mb-0">
          <div className="flex justify-between text-[13px]">
            <span className="text-[var(--ink-700)]">{row.label}</span>
            <span className="font-semibold text-[var(--ink-900)]">
              {row.pct}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[var(--ink-200)] overflow-hidden mt-1">
            <div
              className="h-full bg-[var(--brand-600)]"
              style={{ width: `${row.pct}%` }}
            />
          </div>
        </div>
      ))}
      <div className="mt-5 pt-5 border-t border-[var(--ink-200)]">
        <button className="w-full h-10 rounded-lg bg-[var(--ink-950)] text-white text-[13px] font-semibold flex items-center justify-center gap-2">
          Export auditor packet <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </MockCard>
  );
}
